// src/screens/Rotations.tsx
// Hub "Rotations" : minutes jouées par équipe (total, moyenne par match et par
// quart-temps, ou détail d'un match) + plans de rotation préparés à l'avance.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation as useTranslationOrig } from "react-i18next";
import { ArrowLeftRight, CalendarClock, ClipboardList, Pencil, Plus, Timer, Trash2 } from "lucide-react";
import { supabase } from "../supabaseClient";
import {
  aggregateMinutes,
  bestRotationState,
  formatPlay,
  normalizePlayers,
  RotationPlanRow,
  RotationState,
  RPlayer,
} from "../lib/rotations";
import { Card } from "../components/rotations/RotationUI";

type RawT = (key: string, options?: Record<string, unknown>) => any;
const useTranslationSafe = (ns?: string) =>
  (useTranslationOrig as unknown as (ns?: any) => { t: RawT; i18n: any })(ns);

type TeamLite = { id: string; team_name: string; players: RPlayer[] };
type TrackedMatch = { id: string; date: string | null; opponent: string; state: RotationState };

const TEAM_KEY = "swish:rotations:lastTeam";

export const Rotations = () => {
  const navigate = useNavigate();
  const { t: tRaw, i18n } = useTranslationSafe("rotations");
  const tr = useCallback(
    (key: string, options?: Record<string, unknown>): string => String(tRaw(key, options) ?? ""),
    [tRaw]
  );
  const locale = (i18n?.language || "fr").startsWith("fr") ? "fr-FR" : "en-US";

  const [teams, setTeams] = useState<TeamLite[]>([]);
  const [teamId, setTeamId] = useState<string>("");
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [tab, setTab] = useState<"minutes" | "plans">("minutes");

  const [matches, setMatches] = useState<TrackedMatch[]>([]);
  const [untracked, setUntracked] = useState<{ id: string; date: string | null; opponent: string }[]>([]);
  const [scope, setScope] = useState<string>("all");
  const [loadingData, setLoadingData] = useState(false);

  const [plans, setPlans] = useState<RotationPlanRow[]>([]);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // ---------- Équipes visibles (propriétaire + membre via RLS) ----------
  useEffect(() => {
    (async () => {
      setLoadingTeams(true);
      const { data, error } = await supabase
        .from("teams")
        .select("id, team_name, players")
        .or("archived.is.null,archived.eq.false");
      if (error) console.error("[Rotations] teams error:", error);
      const list: TeamLite[] = (data || [])
        .map((t: any) => ({ id: t.id, team_name: t.team_name ?? "", players: normalizePlayers(t.players) }))
        .sort((a, b) => a.team_name.localeCompare(b.team_name, "fr", { sensitivity: "base" }));
      setTeams(list);
      let saved = "";
      try {
        saved = localStorage.getItem(TEAM_KEY) || "";
      } catch {
        /* ignore */
      }
      setTeamId(list.some((t) => t.id === saved) ? saved : list[0]?.id ?? "");
      setLoadingTeams(false);
    })();
  }, []);

  useEffect(() => {
    if (!teamId) return;
    try {
      localStorage.setItem(TEAM_KEY, teamId);
    } catch {
      /* ignore */
    }
  }, [teamId]);

  // ---------- Matchs + plans de l'équipe ----------
  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    (async () => {
      setLoadingData(true);
      setScope("all");
      const [{ data: matchRows, error: matchErr }, { data: planRows, error: planErr }] = await Promise.all([
        supabase.from("matches").select("*").eq("team_id", teamId).order("date", { ascending: false }),
        supabase.from("rotation_plans").select("*").eq("team_id", teamId).order("updated_at", { ascending: false }),
      ]);
      if (cancelled) return;
      if (matchErr) console.error("[Rotations] matches error:", matchErr);

      const tracked: TrackedMatch[] = [];
      const others: { id: string; date: string | null; opponent: string }[] = [];
      (matchRows || []).forEach((m: any) => {
        const st = bestRotationState(m.id, m.rotations);
        const hasTime = st && Object.keys(st.playMs || {}).length > 0;
        const base = { id: m.id, date: m.date ?? null, opponent: m.opponent ?? "" };
        if (st && hasTime) tracked.push({ ...base, state: st });
        else others.push(base);
      });
      setMatches(tracked);
      setUntracked(others);

      if (planErr) {
        console.error("[Rotations] plans error:", planErr);
        setPlansError(planErr.message);
        setPlans([]);
      } else {
        setPlansError(null);
        setPlans((planRows || []) as RotationPlanRow[]);
      }
      setLoadingData(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const team = teams.find((t) => t.id === teamId);

  const nameOf = useCallback(
    (num: number) => {
      const fromTeam = team?.players.find((p) => p.number === num);
      const fromMatch = matches.map((m) => m.state.squad.find((p) => p.number === num)).find(Boolean);
      const p = fromTeam ?? fromMatch;
      const name = p ? [p.firstName, p.lastName].filter(Boolean).join(" ") : "";
      return name ? `#${num} ${name}` : `#${num}`;
    },
    [team, matches]
  );

  const fmtDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString(locale, { day: "2-digit", month: "short" }) : "—";

  const scopedStates = useMemo(
    () => (scope === "all" ? matches : matches.filter((m) => m.id === scope)).map((m) => m.state),
    [scope, matches]
  );
  const regularPeriods = Math.max(4, ...scopedStates.map((s) => s.settings.periods));
  const periodCols = Array.from({ length: regularPeriods }, (_, i) => i + 1);
  const rows = useMemo(() => aggregateMinutes(scopedStates), [scopedStates]);
  const hasOT = rows.some((r) => Object.keys(r.byPeriod).some((p) => Number(p) > regularPeriods));
  const maxAvg = Math.max(1, ...rows.map((r) => r.totalMs / r.games));
  const single = scope !== "all";

  const otMs = (r: (typeof rows)[number]) =>
    Object.entries(r.byPeriod)
      .filter(([p]) => Number(p) > regularPeriods)
      .reduce((a, [, v]) => a + v.ms, 0);

  const deletePlan = async (id: string) => {
    const { error } = await supabase.from("rotation_plans").delete().eq("id", id);
    if (error) {
      console.error("[Rotations] delete plan error:", error);
      return;
    }
    setPlans((prev) => prev.filter((p) => p.id !== id));
    setConfirmDelete(null);
  };

  return (
    <section className="max-w-6xl mx-auto px-3 sm:px-4 py-6 text-gray-900 dark:text-gray-100">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-orange-600 dark:text-orange-400">{tr("hub.title")}</h1>
          <p className="text-sm text-gray-500">{tr("hub.subtitle")}</p>
        </div>
        {teams.length > 0 && (
          <select
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            className="rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium"
            aria-label={tr("hub.team")}
          >
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.team_name || t.id}
              </option>
            ))}
          </select>
        )}
      </div>

      {loadingTeams ? (
        <p className="text-gray-500">{tr("loading")}</p>
      ) : teams.length === 0 ? (
        <Card title={tr("hub.noTeamTitle")}>
          <p className="text-sm text-gray-500 mb-3">{tr("hub.noTeam")}</p>
          <button
            onClick={() => navigate("/teams/new")}
            className="px-4 py-2 rounded-xl bg-orange-600 text-white text-sm font-semibold"
          >
            {tr("hub.createTeam")}
          </button>
        </Card>
      ) : (
        <>
          <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1 mb-4">
            <TabBtn active={tab === "minutes"} onClick={() => setTab("minutes")}>
              <Timer size={16} /> {tr("hub.tabMinutes")}
            </TabBtn>
            <TabBtn active={tab === "plans"} onClick={() => setTab("plans")}>
              <ClipboardList size={16} /> {tr("hub.tabPlans")}
              {plans.length > 0 && <span className="text-xs opacity-80">({plans.length})</span>}
            </TabBtn>
          </div>

          {loadingData ? (
            <p className="text-gray-500">{tr("loading")}</p>
          ) : tab === "minutes" ? (
            <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
              <Card
                title={single ? tr("stats.matchTitle") : tr("stats.title")}
                action={
                  matches.length > 0 ? (
                    <select
                      value={scope}
                      onChange={(e) => setScope(e.target.value)}
                      className="rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-1.5 text-sm max-w-[55%]"
                    >
                      <option value="all">{tr("stats.allMatches", { n: matches.length })}</option>
                      {matches.map((m) => (
                        <option key={m.id} value={m.id}>
                          {fmtDate(m.date)} · {m.opponent || "?"}
                        </option>
                      ))}
                    </select>
                  ) : undefined
                }
              >
                {rows.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">{tr("stats.empty")}</p>
                ) : (
                  <>
                    <p className="text-xs text-gray-500 mb-3">
                      {single ? tr("stats.matchHint") : tr("stats.hint")}
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200 dark:border-gray-800">
                            <th className="py-2 pr-3">{tr("analysis.player")}</th>
                            {!single && <th className="py-2 px-2 text-right">{tr("stats.games")}</th>}
                            <th className="py-2 px-2 text-right">{tr("analysis.total")}</th>
                            {!single && <th className="py-2 px-2 min-w-[150px]">{tr("stats.avg")}</th>}
                            {periodCols.map((p) => (
                              <th key={p} className="py-2 px-2 text-right">
                                {tr("period.short", { n: p })}
                              </th>
                            ))}
                            {hasOT && <th className="py-2 px-2 text-right">{tr("stats.ot")}</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => {
                            const avg = r.totalMs / r.games;
                            const div = single ? 1 : r.games;
                            return (
                              <tr key={r.number} className="border-b border-gray-100 dark:border-gray-800/60">
                                <td className="py-2 pr-3 whitespace-nowrap">{nameOf(r.number)}</td>
                                {!single && <td className="py-2 px-2 text-right tabular-nums">{r.games}</td>}
                                <td className="py-2 px-2 text-right tabular-nums font-semibold">
                                  {formatPlay(r.totalMs)}
                                </td>
                                {!single && (
                                  <td className="py-2 px-2">
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                                        <div
                                          className="h-full bg-orange-500"
                                          style={{ width: `${(avg / maxAvg) * 100}%` }}
                                        />
                                      </div>
                                      <span className="tabular-nums font-semibold w-12 text-right">
                                        {formatPlay(avg)}
                                      </span>
                                    </div>
                                  </td>
                                )}
                                {periodCols.map((p) => (
                                  <td key={p} className="py-2 px-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
                                    {r.byPeriod[p] ? formatPlay(r.byPeriod[p].ms / div) : "–"}
                                  </td>
                                ))}
                                {hasOT && (
                                  <td className="py-2 px-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
                                    {otMs(r) ? formatPlay(otMs(r) / div) : "–"}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </Card>

              <Card title={tr("stats.matchesTitle")}>
                {matches.length === 0 && untracked.length === 0 && (
                  <p className="text-sm text-gray-500 italic">{tr("stats.noMatches")}</p>
                )}
                <ul className="space-y-1">
                  {[...matches.map((m) => ({ ...m, tracked: true })), ...untracked.map((m) => ({ ...m, tracked: false }))]
                    .slice(0, 20)
                    .map((m) => (
                      <li key={m.id}>
                        <button
                          onClick={() => navigate(`/rotations/${m.id}`)}
                          className="w-full flex items-center justify-between gap-2 rounded-lg px-2 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <span className="min-w-0 truncate">
                            <span className="text-gray-500 mr-2">{fmtDate(m.date)}</span>
                            {m.opponent || "?"}
                          </span>
                          {m.tracked ? (
                            <span className="shrink-0 text-[10px] font-semibold uppercase text-teal-600">
                              {tr("stats.tracked")}
                            </span>
                          ) : (
                            <ArrowLeftRight size={14} className="shrink-0 text-gray-400" />
                          )}
                        </button>
                      </li>
                    ))}
                </ul>
                <p className="text-xs text-gray-500 mt-3">{tr("stats.matchesHint")}</p>
              </Card>
            </div>
          ) : (
            <Card
              title={tr("plans.title")}
              action={
                <button
                  onClick={() => navigate(`/rotations/plan/new?team=${teamId}`)}
                  disabled={!!plansError}
                  className="inline-flex items-center gap-1 rounded-xl bg-orange-600 text-white px-3 py-2 text-sm font-semibold disabled:opacity-40"
                >
                  <Plus size={16} /> {tr("plans.new")}
                </button>
              }
            >
              {plansError ? (
                <p className="text-sm text-red-600">{tr("plans.tableMissing")}</p>
              ) : plans.length === 0 ? (
                <p className="text-sm text-gray-500 italic">{tr("plans.empty")}</p>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                  {plans.map((p) => {
                    const assigned = (p.data?.segments || []).reduce((a, s) => a + (s.end - s.start), 0);
                    const target =
                      (p.data?.settings?.periods ?? 4) * (p.data?.settings?.periodMinutes ?? 10) * 60 * 5;
                    return (
                      <li key={p.id} className="py-3 flex flex-wrap items-center gap-3">
                        <button
                          onClick={() => navigate(`/rotations/plan/${p.id}`)}
                          className="flex-1 min-w-0 text-left"
                        >
                          <span className="block font-semibold truncate">{p.name || tr("planner.untitled")}</span>
                          <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                            <CalendarClock size={12} />
                            {new Date(p.updated_at).toLocaleDateString(locale)}
                            {" · "}
                            {tr("plans.assigned", {
                              done: formatPlay(assigned * 1000),
                              total: formatPlay(target * 1000),
                            })}
                          </span>
                        </button>
                        {confirmDelete === p.id ? (
                          <span className="flex gap-2">
                            <button
                              onClick={() => deletePlan(p.id)}
                              className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold"
                            >
                              {tr("plans.confirmDelete")}
                            </button>
                            <button
                              onClick={() => setConfirmDelete(null)}
                              className="px-3 py-1.5 rounded-lg border text-xs"
                            >
                              {tr("actions.cancel")}
                            </button>
                          </span>
                        ) : (
                          <span className="flex gap-1">
                            <button
                              onClick={() => navigate(`/rotations/plan/${p.id}`)}
                              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                              aria-label={tr("plans.open")}
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => setConfirmDelete(p.id)}
                              className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                              aria-label={tr("plans.delete")}
                            >
                              <Trash2 size={16} />
                            </button>
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          )}
        </>
      )}
    </section>
  );
};

const TabBtn = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg font-medium ${
      active ? "bg-orange-600 text-white" : "text-gray-700 dark:text-gray-200"
    }`}
  >
    {children}
  </button>
);

export default Rotations;
